import {
  derivePresence,
  fieldProblemReasonLabels,
  helpRequestReasonLabels,
  OPERATIONS_CENTER_STALE_MS,
  segmentTrack,
  trackDistanceMeters,
  watchIncidentKey,
  type CenterOperator,
  type DerivedPresence,
  type OperationsCenterSnapshot,
  type OperationTask,
  type OperationTracks,
  type TrackPoint,
} from '@groupe-reca/contracts'

// Règles d'affichage du nouveau Centre des opérations (ADR-019, docs/22-Centre-des-operations.md). Aucune donnée
// n'est inventée : sans position reçue, aucun repère; « en cours » vient seulement de la tablette; une coupure
// réseau n'est jamais une fin de mission.

// ------------------------------------------------------------------ Erreurs

const errorMessages: Record<string, string> = {
  MISSION_READ_FORBIDDEN: 'Votre rôle ne permet pas de consulter le centre des opérations.',
  MISSION_SUPERVISE_FORBIDDEN: 'Votre rôle ne permet pas cette action.',
  OPERATION_NOT_FOUND: 'Opération introuvable.',
  OPERATION_NOT_OPEN: 'L’opération n’est plus ouverte : aucune correction silencieuse du bilan.',
  OPERATION_NOT_READY: 'Clôture impossible : des missions, résidences, offres ou conflits restent à traiter.',
  OPERATION_VERSION_CONFLICT:
    'L’opération a changé entre-temps. Données rafraîchies : vérifiez puis recommencez.',
  OPERATION_ANOTHER_OPEN: 'Une autre opération est ouverte : traitez la correction à part.',
  OPERATION_NOT_CLOSED: 'Seule une opération clôturée peut être rouverte.',
  OPERATION_TASK_VERSION_CONFLICT:
    'La résidence a changé entre-temps. Données rafraîchies : vérifiez puis recommencez.',
  OPERATION_TASK_NOT_PROBLEM: 'Cette résidence n’est plus en problème.',
  OPERATION_TASK_NOT_UNRESOLVED: 'Cette résidence est déjà réglée.',
  OPERATION_TASK_NOT_DONE: 'Seule une résidence déneigée peut être remise à faire.',
  OPERATION_RESOLVE_REASON_REQUIRED: 'Un motif est obligatoire (3 caractères au moins).',
  OPERATION_RESET_REASON_REQUIRED: 'Un motif est obligatoire (3 caractères au moins).',
  OPERATION_REASSIGN_REASON_REQUIRED: 'Un motif est obligatoire (3 caractères au moins).',
  OPERATION_REOPEN_REASON_REQUIRED: 'Un motif est obligatoire (3 caractères au moins).',
  OPERATION_TARGET_WITHOUT_MISSION: 'Cet opérateur n’a pas de mission ouverte.',
  MISSION_NOT_IN_OPERATION: 'La mission de cet opérateur n’appartient pas à cette opération.',
  OFFER_TASK_NOT_ELIGIBLE:
    'Une résidence n’est plus admissible au renfort (en cours, déjà offerte ou réglée).',
  OFFER_RECIPIENT_NOT_ELIGIBLE: 'Un destinataire n’est pas admissible.',
  OFFER_ALREADY_CLAIMED: 'Offre déjà prise en charge : utilisez une réattribution.',
  MISSION_CANCEL_REASON_REQUIRED: 'Un motif est obligatoire (3 caractères au moins).',
  MISSION_HAS_WORK: 'Du travail est enregistré : ce n’est pas un démarrage accidentel.',
  MISSION_NOT_OPEN: 'Cette mission n’est plus ouverte.',
  OPERATION_CONFLICT_NOTE_REQUIRED: 'Une note est obligatoire (3 caractères au moins).',
  // Module Opérations (bilan, clôture avec exceptions, annulation).
  OPERATION_CHANGED: 'L’opération a changé. Vérifiez le bilan mis à jour.',
  OPERATION_FINGERPRINT_REQUIRED: 'Relisez le bilan avant de confirmer.',
  OPERATION_HAS_OPEN_MISSIONS:
    'Une mission est encore ouverte : terminez-la ou annulez-la depuis sa fiche Missions d’abord.',
  OPERATION_HAS_OPEN_CONFLICTS: 'Un conflit attend une décision explicite dans le Centre des opérations.',
  OPERATION_HAS_UNRESOLVED_TASKS: 'Des interventions restent à faire.',
  OPERATION_HAS_WORK:
    'Du travail est enregistré dans cette opération : ce n’est pas un faux départ. Utilisez la clôture avec exceptions.',
  OPERATION_OFFERS_ACTIVE: 'Des offres de renfort sont actives : leur révocation doit être confirmée.',
  OPERATION_NO_EXCEPTIONS: 'Aucune exception : utilisez la clôture normale.',
  OPERATION_EXCEPTIONS_REASON_REQUIRED: 'Un motif est obligatoire (3 caractères au moins).',
  OPERATION_CANCEL_REASON_REQUIRED: 'Un motif est obligatoire (3 caractères au moins).',
  OPERATIONS_VIEW_INVALID: 'Vue inconnue.',
  ROUTE_PASS_ROUTES_REQUIRED: 'Choisissez au moins une route.',
  ROUTE_PASS_ROUTE_NOT_ENGAGED:
    'Cette route n’a pas encore été engagée : son premier départ crée le passage 1.',
  ROUTE_PASS_ALREADY_PREPARED: 'Un passage est déjà préparé pour cette route.',
  ROUTE_PASS_MISSION_OPEN: 'Une mission est en cours sur cette route : attendez sa fermeture.',
  ROUTE_PASS_PREVIOUS_UNFINISHED:
    'Le passage précédent n’est pas terminé : proposez d’abord une reprise des résidences restantes.',
  ROUTE_PASS_NOT_PREPARED: 'Ce passage a déjà été engagé ou annulé.',
  ROUTE_PASS_CANCEL_REASON_REQUIRED: 'Un motif est obligatoire (3 caractères au moins).',
}

export class OperationsCenterError extends Error {
  constructor(public readonly code: string) {
    super(
      errorMessages[code] ?? 'L’action n’a pas pu être confirmée par le serveur. Réessayez dans un instant.',
    )
    this.name = 'OperationsCenterError'
  }
}

export function operationsErrorCode(message: string | undefined): string {
  const code = message?.trim() ?? ''
  return /^[A-Z][A-Z_]+$/.test(code) ? code : 'UNEXPECTED'
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Erreur inattendue.'
}

// ------------------------------------------------------------------ Fraîcheur de la supervision

export type Freshness = 'LIVE' | 'LOST' | 'LOADING'

export function supervisionFreshness(lastSuccessMs: number | null, nowMs: number): Freshness {
  if (lastSuccessMs === null) return 'LOADING'
  return nowMs - lastSuccessMs <= OPERATIONS_CENTER_STALE_MS ? 'LIVE' : 'LOST'
}

export function agoLabel(ms: number | null): string {
  if (ms === null) return '—'
  const seconds = Math.max(1, Math.round(ms / 1000))
  if (seconds < 60) return `il y a ${String(seconds)} s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `il y a ${String(minutes)} min`
  const hours = Math.floor(minutes / 60)
  return `il y a ${String(hours)} h ${String(minutes % 60).padStart(2, '0')}`
}

// ------------------------------------------------------------------ Opérateurs

export interface OperatorView {
  operator: CenterOperator
  derived: DerivedPresence
  /** Visible au centre : connecté (même sans mission) ou en mission. */
  listed: boolean
  isReinforcement: boolean
}

const workOrder: Record<DerivedPresence['work'], number> = { MISSION: 0, AVAILABLE: 1, PAUSED: 2, NONE: 3 }

export function operatorViews(operators: CenterOperator[], serverNowMs: number): OperatorView[] {
  return operators
    .map((operator) => {
      const derived = derivePresence(operator, serverNowMs)
      return {
        operator,
        derived,
        listed:
          operator.mission !== null ||
          derived.connection === 'ONLINE' ||
          derived.connection === 'INTERRUPTED',
        isReinforcement: operator.mission?.kind === 'REINFORCEMENT',
      }
    })
    .sort(
      (a, b) =>
        workOrder[a.derived.work] - workOrder[b.derived.work] ||
        (a.operator.name ?? '').localeCompare(b.operator.name ?? '', 'fr'),
    )
}

export const workLabels: Record<DerivedPresence['work'], string> = {
  MISSION: 'En mission',
  AVAILABLE: 'Disponible pour aider',
  PAUSED: 'En pause',
  NONE: 'Sans mission',
}

export function connectionLabel(derived: DerivedPresence): string {
  switch (derived.connection) {
    case 'ONLINE':
      return 'En ligne'
    case 'INTERRUPTED':
      return 'Connexion interrompue'
    case 'EXPIRED':
      return 'Session expirée'
    case 'DISCONNECTED':
      return 'Déconnecté'
    case 'NEVER':
      return 'Présence inconnue'
  }
}

export function positionLabel(derived: DerivedPresence): string {
  if (derived.position === 'NONE') return 'Position indisponible'
  if (derived.position === 'STALE') return `Dernière position reçue ${agoLabel(derived.positionAgeMs)}`
  return `GPS ${agoLabel(derived.positionAgeMs)}`
}

// ------------------------------------------------------------------ File À surveiller

export type WatchKind = 'HELP' | 'PROBLEM' | 'SIGNAL' | 'CONFLICT' | 'REASSIGN'

export interface WatchItem {
  key: string
  kind: WatchKind
  priority: 'critical' | 'warning'
  at: string
  title: string
  subtitle: string
  note: string | null
  ack: { byName: string | null; at: string } | null
  taskId: string | null
  employeeId: string | null
  helpRequestId: string | null
  conflictId: string | null
}

/** Premier passage ou reprise (une tentative a déjà échoué, ou une nouvelle tentative est demandée). */
export function passLabel(task: OperationTask): string {
  return task.attemptCount > 1 || task.retryRequestedAt ? 'À reprendre' : 'Premier passage'
}

/**
 * Une alerte par incident (clé stable), triée par priorité : demandes d'aide non prises en charge, puis le reste.
 * « Je m'en occupe » ajoute un auteur, jamais une résolution : l'alerte reste jusqu'au règlement réel.
 */
export function watchItems(snapshot: OperationsCenterSnapshot, serverNowMs: number): WatchItem[] {
  const acks = new Map(snapshot.acks.map((ack) => [ack.incidentKey, ack]))
  const ackFor = (key: string) => {
    const ack = acks.get(key)
    return ack ? { byName: ack.ackedByName, at: ack.ackedAt } : null
  }
  const base = { note: null, taskId: null, employeeId: null, helpRequestId: null, conflictId: null }
  const items: WatchItem[] = []

  for (const help of snapshot.helpRequests) {
    items.push({
      ...base,
      key: `help:${help.id}`,
      kind: 'HELP',
      priority: help.status === 'SENT' ? 'critical' : 'warning',
      at: help.createdAt,
      title: `Demande d’aide — ${helpRequestReasonLabels[help.reason]}`,
      subtitle: help.operatorName ?? 'Opérateur',
      note: help.comment,
      ack: help.acknowledgedAt ? { byName: help.acknowledgedByName, at: help.acknowledgedAt } : null,
      employeeId: help.employeeId,
      helpRequestId: help.id,
    })
  }
  for (const task of snapshot.tasks) {
    if (task.status === 'PROBLEM') {
      const key = watchIncidentKey.task(task.id, task.attemptCount)
      const reason = task.lastProblem?.reasonCode
        ? fieldProblemReasonLabels[task.lastProblem.reasonCode]
        : 'Problème signalé'
      items.push({
        ...base,
        key,
        kind: 'PROBLEM',
        priority: 'warning',
        at: task.lastProblem?.at ?? task.updatedAt,
        title: task.snapshot.formattedAddress ?? task.snapshot.addressLine1 ?? 'Résidence',
        subtitle: `${reason} · ${task.routeName ?? 'Route'} · ${passLabel(task)}`,
        note: task.lastProblem?.note ?? task.lastProblem?.reasonText ?? null,
        ack: ackFor(key),
        taskId: task.id,
      })
    } else if (task.needsReassignment) {
      const key = `task:${task.id}:reassign`
      items.push({
        ...base,
        key,
        kind: 'REASSIGN',
        priority: 'warning',
        at: task.updatedAt,
        title: task.snapshot.formattedAddress ?? task.snapshot.addressLine1 ?? 'Résidence',
        subtitle: `À réattribuer · ${task.routeName ?? 'Route'}`,
        ack: ackFor(key),
        taskId: task.id,
      })
    }
  }
  for (const view of operatorViews(snapshot.operators, serverNowMs)) {
    const { operator, derived } = view
    if (!operator.mission) continue
    const silent =
      derived.connection === 'INTERRUPTED' ||
      derived.connection === 'EXPIRED' ||
      (derived.connection === 'NEVER' && derived.position !== 'RECENT')
    if (!silent) continue
    const since =
      operator.presence?.lastHeartbeatAt ?? operator.position?.receivedAt ?? operator.mission.startedAt
    const key = watchIncidentKey.signal(operator.employeeId, since)
    items.push({
      ...base,
      key,
      kind: 'SIGNAL',
      priority: 'warning',
      at: since,
      title: 'Tablette sans nouvelles données',
      subtitle: `${operator.name ?? 'Opérateur'} · ${operator.mission.equipmentLabel ?? ''} · ${positionLabel(derived)}`,
      note: 'La tablette ne transmet pas sa progression. Mission toujours ouverte; aucune avance n’est supposée.',
      ack: ackFor(key),
      employeeId: operator.employeeId,
    })
  }
  for (const conflict of snapshot.conflicts) {
    const key = watchIncidentKey.conflict(conflict.id)
    items.push({
      ...base,
      key,
      kind: 'CONFLICT',
      priority: 'warning',
      at: conflict.createdAt,
      title: `Attribution à vérifier — ${conflict.address ?? 'résidence'}`,
      subtitle: `${conflict.operatorName ?? 'Une tablette'} a rendu compte après un transfert`,
      note:
        conflict.kind === 'COMPLETE_AFTER_TRANSFER'
          ? 'Terminée sur l’ancienne tablette.'
          : 'Problème signalé sur l’ancienne tablette.',
      ack: ackFor(key),
      taskId: conflict.taskId,
      conflictId: conflict.id,
    })
  }
  const rank = (item: WatchItem) => (item.priority === 'critical' ? 0 : 1)
  return items.sort((a, b) => rank(a) - rank(b) || Date.parse(b.at) - Date.parse(a.at))
}

// ------------------------------------------------------------------ Trajets

/** Palette des traces par équipement, distincte des couleurs de route (légende séparée). */
export const traceColors = [
  '#5ee0e6',
  '#f472b6',
  '#c4e05e',
  '#ff9a5c',
  '#e2e8f0',
  '#7dd3fc',
  '#fbbf24',
  '#a78bfa',
]

export function traceColorFor(equipmentKey: string): string {
  let hash = 0
  for (const char of equipmentKey) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  return traceColors[hash % traceColors.length] ?? '#e2e8f0'
}

export interface MissionTrace {
  missionId: string
  equipmentKey: string
  label: string
  color: string
  /** Tronçons réels : une interruption de collecte coupe la ligne, jamais reliée. */
  segments: TrackPoint[][]
  /** Somme des tronçons acceptés (m) : estimation, jamais calculée sur la ligne décalée à l'écran. */
  distanceMeters: number
  discarded: number
}

export function missionTraces(tracks: OperationTracks): MissionTrace[] {
  return tracks.missions.map((mission) => {
    const segmented = segmentTrack(
      mission.points.map((point) => ({
        id: point.id,
        capturedAt: point.capturedAt,
        latitude: point.latitude,
        longitude: point.longitude,
        accuracy: point.accuracy,
      })),
      [...mission.pauses, ...mission.gaps.map((gap) => ({ startedAt: gap.startedAt, endedAt: gap.endedAt }))],
    )
    const distanceMeters = segmented.segments.reduce(
      (total, segment) =>
        total +
        segment.slice(1).reduce((sum, point, index) => {
          const previous = segment[index]
          return previous ? sum + trackDistanceMeters(previous, point) : sum
        }, 0),
      0,
    )
    const equipmentKey = mission.equipmentId ?? mission.missionId
    return {
      missionId: mission.missionId,
      equipmentKey,
      label: `${mission.equipmentLabel ?? 'Équipement'} · ${mission.operatorName ?? ''}`.trim(),
      color: traceColorFor(equipmentKey),
      segments: segmented.segments,
      distanceMeters,
      discarded: segmented.discarded.inaccurate + segmented.discarded.outliers,
    }
  })
}

export function formatKm(meters: number): string {
  return `${(meters / 1000).toFixed(1).replace('.', ',')} km`
}

export function formatHours(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1).replace('.', ',')} h`
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—'
  const minutes = Math.round(seconds / 60)
  return `${String(Math.floor(minutes / 60))} h ${String(minutes % 60).padStart(2, '0')}`
}
